import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from './Button'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-6">
          <div className="max-w-md text-center">
            <h1 className="font-serif text-4xl text-foreground">Something went wrong</h1>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
              An unexpected error occurred. You can try refreshing the page or going back to the dashboard.
            </p>
            {this.state.error && (
              <p className="mt-4 font-mono text-xs text-error bg-error/10 rounded-lg px-4 py-3 text-left break-all">
                {this.state.error.message}
              </p>
            )}
            <div className="mt-8 flex justify-center gap-3">
              <Button variant="ghost" onClick={() => window.location.href = '/'}>
                Dashboard
              </Button>
              <Button variant="primary" onClick={() => window.location.reload()}>
                Reload page
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
